// Director notes (`message_director_notes`): per-user authoring annotations
// attached to roleplay messages. Owns its own table, so it lives apart from
// message CRUD; messages.ts and conversations.ts consume the attach helpers.
import { supabase, uid } from './shared'
import { mapDirectorNoteFromSupabase } from '../../mappers/messages'
import type { MessageDirectorNoteUpdateData } from '../../contract'

export interface MessageLike {
  id: number | string
  conversation_id: number | string
  t?: string
  created_at?: string
  updated_at?: string
  director_note?: unknown
}

interface DirectorNoteRowLike {
  id: string | number
  message_id: string | number
  visibility_count?: number | null
  created_at?: string
  updated_at?: string
}

export function sanitizeDirectorNotePayload(
  messageId: number | string,
  userId: string,
  noteData: MessageDirectorNoteUpdateData | null | undefined,
) {
  if (!noteData) return null;
  const content = noteData.content ?? '';
  if (!content.trim()) return null;
  const placement = noteData.placement === 'prepend' ? 'prepend' : 'append';
  const visibilityCount = noteData.visibility_count == null ? null : Number(noteData.visibility_count);
  return {
    message_id: Number(messageId),
    user_id: userId,
    content,
    placement,
    visibility_count: visibilityCount !== null && Number.isFinite(visibilityCount) && visibilityCount >= 1
      ? Math.floor(visibilityCount)
      : null,
  };
}

async function getDirectorNotesByMessageIds(messageIds: Array<number | string | null | undefined> = [], userId: string) {
  const normalizedIds = Array.from(
    new Set((messageIds || []).map((value) => Number(value)).filter(Number.isFinite)),
  );
  if (!normalizedIds.length) {
    return new Map();
  }
  const { data, error } = await supabase
    .from('message_director_notes')
    .select('*')
    .eq('user_id', userId)
    .in('message_id', normalizedIds);
  if (error) throw error;
  return new Map(
    (data || []).map((note: DirectorNoteRowLike) => [
      Number(note.message_id),
      mapDirectorNoteFromSupabase(note as Parameters<typeof mapDirectorNoteFromSupabase>[0]),
    ]),
  );
}

export async function attachDirectorNotesToMessages<T extends MessageLike>(
  messages: T[] = [],
  userId: string,
): Promise<Array<T & { director_note: ReturnType<typeof mapDirectorNoteFromSupabase> }>> {
  const notesByMessageId = await getDirectorNotesByMessageIds(
    messages.map((message) => message?.id),
    userId,
  );
  return (messages || []).map((message) => ({
    ...message,
    director_note: notesByMessageId.get(Number(message.id)) ?? null,
  }));
}

export async function attachDirectorNoteToMessage<T extends MessageLike>(
  message: T | null | undefined,
  userId: string,
): Promise<(T & { director_note: ReturnType<typeof mapDirectorNoteFromSupabase> }) | T | null | undefined> {
  if (!message) return message;
  const [enriched] = await attachDirectorNotesToMessages([message], userId);
  return enriched || message;
}

export async function updateMessageDirectorNote(messageId: number | string, noteData: MessageDirectorNoteUpdateData) {
  const userId = await uid();
  const payload = sanitizeDirectorNotePayload(messageId, userId, noteData);

  if (!payload) {
    await deleteMessageDirectorNote(messageId);
    return { director_note: null };
  }

  const { data, error } = await supabase
    .from('message_director_notes')
    .upsert(payload, { onConflict: 'message_id' })
    .select('*')
    .single();

  if (error) throw error;

  return {
    director_note: mapDirectorNoteFromSupabase(data),
  };
}

export async function deleteMessageDirectorNote(messageId: number | string) {
  const userId = await uid();

  const { error } = await supabase
    .from('message_director_notes')
    .delete()
    .eq('message_id', messageId)
    .eq('user_id', userId);

  if (error) throw error;

  return { message: 'Director note deleted successfully' };
}
